import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { Subject, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { CidadeService } from 'src/app/proxy/cidades/cidade.service';
import { CidadeDto } from 'src/app/proxy/cidades/models';

@Component({
  selector: 'app-cidade-select',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule],
  templateUrl: './cidade-select.component.html',
  styleUrls: ['./cidade-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CidadeSelectComponent),
      multi: true,
    },
  ],
})
export class CidadeSelectComponent implements OnInit, OnDestroy {
  items: CidadeDto[] = [];
  loading = false;

  @Input() ariaLabel = 'Cidade';
  @Input() inputId?: string;
  @Input() notFoundText = 'Nenhuma cidade encontrada';
  @Input() loadingText = 'Carregando...';
  @Input() clearable = true;
  @Input() searchable = true;
  @Input() virtualScroll = true;
  @Input() minTermLength = 1;
  @Input() pageSize = 100;

  value: number | null = null;
  private selectedItem: CidadeDto | null = null;
  isDisabled = false;

  private readonly destroy$ = new Subject<void>();
  readonly search$ = new Subject<string>();

  private onChange: (value: number | null) => void = () => undefined;
  onTouched: () => void = () => undefined;

  constructor(
    private readonly cidadeService: CidadeService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadInitial();

    this.search$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => (this.loading = true)),
        switchMap(term =>
          this.fetchCidades(term).pipe(
            catchError(() => of([])),
            finalize(() => (this.loading = false))
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        this.items = this.mergeSelectedItem(result);
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(value: number | null): void {
    this.value = this.normalizeValue(value);
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  onValueChange(value: number | CidadeDto | string | null): void {
    const normalizedValue = this.extractId(value);
    this.value = normalizedValue;
    this.selectedItem = this.findSelectedItem(normalizedValue, value);
    this.items = this.mergeSelectedItem(this.items);
    this.onChange(this.value);
    this.cdr.markForCheck();
  }

  private loadInitial(): void {
    this.loading = true;
    this.cidadeService
      .getList({
        skipCount: 0,
        maxResultCount: this.pageSize,
      })
      .pipe(
        finalize(() => (this.loading = false)),
        takeUntil(this.destroy$),
        catchError(() => of({ items: [] }))
      )
      .subscribe(result => {
        this.items = this.mergeSelectedItem(result.items);
        this.cdr.markForCheck();
      });
  }

  private fetchCidades(term: string) {
    const trimmed = term?.trim();
    return this.cidadeService
      .getList({
        skipCount: 0,
        maxResultCount: this.pageSize,
        filterText: trimmed || undefined,
        nome: trimmed || undefined,
      })
      .pipe(map(result => result.items));
  }

  compareById = (
    a: number | string | CidadeDto | null,
    b: number | string | CidadeDto | null
  ): boolean => this.extractId(a) === this.extractId(b);

  private extractId(value: number | CidadeDto | string | null | undefined): number | null {
    if (value && typeof value === 'object') {
      return this.normalizeValue(value.id);
    }
    return this.normalizeValue(value as number | string | null | undefined);
  }

  private normalizeValue(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const numericValue = typeof value === 'number' ? value : Number(value);
    return Number.isNaN(numericValue) ? null : numericValue;
  }

  private findSelectedItem(
    value: number | null,
    rawValue?: number | CidadeDto | string | null
  ): CidadeDto | null {
    if (rawValue && typeof rawValue === 'object') {
      return rawValue;
    }
    if (value === null) {
      return null;
    }
    return (
      this.items.find(item => this.normalizeValue(item.id) === value) ??
      this.selectedItem
    );
  }

  private mergeSelectedItem(items: CidadeDto[]): CidadeDto[] {
    if (!this.selectedItem) {
      return items;
    }
    const hasSelectedItem = items.some(item => item.id === this.selectedItem.id);
    return hasSelectedItem ? items : [this.selectedItem, ...items];
  }
}
